(() => {
  "use strict";

  const MAIN_SELECTOR = "#MainContent";
  const CATEGORY_LINK_SELECTOR = ".js-ht-category-link";
  const FILTER_LINK_SELECTOR = ".tps__link-filter";

  let currentRequest = null;
  let requestCounter = 0;

  const priceMinCache = new Map();

  /* =========================================================
     GENERAL HELPERS
  ========================================================= */

  function getMainContent(doc = document) {
    return (
      doc.querySelector(MAIN_SELECTOR) ||
      doc.querySelector('main[role="main"]') ||
      doc.querySelector("main")
    );
  }

  function setLoading(status) {
    document.documentElement.classList.toggle(
      "ht-collection-loading",
      status
    );

    const mainContent = getMainContent();

    if (mainContent) {
      mainContent.setAttribute(
        "aria-busy",
        status ? "true" : "false"
      );
    }

    let loader = document.getElementById(
      "htCollectionAjaxLoader"
    );

    if (status && !loader) {
      loader = document.createElement("div");

      loader.id = "htCollectionAjaxLoader";

      loader.setAttribute(
        "role",
        "status"
      );

      loader.setAttribute(
        "aria-label",
        "Loading products"
      );

      document.body.appendChild(loader);
    }

    if (!status && loader) {
      loader.remove();
    }
  }

  function updateCanonical(parsedDocument) {
    const currentCanonical =
      document.querySelector(
        'link[rel="canonical"]'
      );

    const newCanonical =
      parsedDocument.querySelector(
        'link[rel="canonical"]'
      );

    if (
      currentCanonical &&
      newCanonical
    ) {
      currentCanonical.href =
        newCanonical.href;
    }
  }

  function normalizePath(path) {
    let normalized = path || "/";

    try {
      normalized =
        decodeURIComponent(normalized);
    } catch (error) {}

    normalized =
      normalized.replace(/\/+$/, "");

    return normalized || "/";
  }

  function updateActiveCategoryLinks(
    root = document
  ) {
    const links = Array.from(
      root.querySelectorAll(
        CATEGORY_LINK_SELECTOR
      )
    );

    if (!links.length) return;

    const currentPath =
      normalizePath(
        window.location.pathname
      );

    links.forEach((link) => {
      link.classList.remove(
        "is-active"
      );

      link.removeAttribute(
        "aria-current"
      );
    });

    const matches =
      links.filter((link) => {
        try {
          const url =
            new URL(
              link.href,
              window.location.href
            );

          return (
            normalizePath(
              url.pathname
            ) === currentPath
          );
        } catch (error) {
          return false;
        }
      });

    if (!matches.length) return;

    const activeLink =
      matches.find((link) =>
        link.classList.contains(
          "ht-category-tree__child-link"
        )
      ) || matches[0];

    activeLink.classList.add(
      "is-active"
    );

    activeLink.setAttribute(
      "aria-current",
      "page"
    );

    const parentItem =
      activeLink.closest(
        ".ht-category-tree__item"
      );

    if (!parentItem) return;

    parentItem.classList.add(
      "is-open"
    );

    const toggle =
      parentItem.querySelector(
        ":scope > .ht-category-tree__row .ht-category-tree__toggle"
      );

    const children =
      parentItem.querySelector(
        ":scope > .ht-category-tree__children"
      );

    if (toggle) {
      toggle.setAttribute(
        "aria-expanded",
        "true"
      );
    }

    if (children) {
      children.hidden = false;
    }
  }

  function notifyThemeAfterAjax(
    newMain
  ) {
    if (newMain) {
      newMain.dispatchEvent(
        new CustomEvent(
          "shopify:section:load",
          {
            bubbles: true,
            detail: {
              sectionId:
                newMain
                  .querySelector(
                    "[data-section-id]"
                  )
                  ?.dataset
                  .sectionId || null
            }
          }
        )
      );
    }

    document.dispatchEvent(
      new CustomEvent(
        "ht:collection:loaded",
        {
          bubbles: true,
          detail: {
            main: newMain || null
          }
        }
      )
    );

    window.dispatchEvent(
      new Event("resize")
    );
  }

  /* =========================================================
     COLLECTION AJAX
  ========================================================= */

  async function loadCollectionPage(
  requestedUrl,
  options = {}
) {
  const {
    updateHistory = true,
    scrollToProducts = true
  } = options;

  const currentMain = getMainContent();

  if (!currentMain) {
    window.location.href = requestedUrl;
    return;
  }

  /*
   * IMPORTANT:
   * Find ONLY the Shopify section
   * that contains the collection sidebar.
   *
   * We do NOT replace #MainContent anymore.
   */
  const currentSidebar =
    currentMain.querySelector(
      ".tp-shop-sidebar"
    );

  const currentCollectionSection =
    currentSidebar
      ? currentSidebar.closest(
          ".shopify-section"
        )
      : null;

  /*
   * If we can't safely identify
   * the collection section, use
   * normal navigation instead of
   * breaking other sliders.
   */
  if (!currentCollectionSection) {
    window.location.href = requestedUrl;
    return;
  }

  if (currentRequest) {
    currentRequest.abort();
  }

  const controller =
    new AbortController();

  currentRequest = controller;

  const thisRequest =
    ++requestCounter;

  setLoading(true);

  try {

    const response = await fetch(
      requestedUrl,
      {
        method: "GET",
        credentials: "same-origin",

        headers: {
          Accept: "text/html",
          "X-Requested-With":
            "XMLHttpRequest"
        },

        signal: controller.signal
      }
    );

    if (!response.ok) {
      throw new Error(
        "Collection AJAX request failed"
      );
    }

    const html =
      await response.text();

    if (
      thisRequest !==
      requestCounter
    ) {
      return;
    }

    const parsedDocument =
      new DOMParser().parseFromString(
        html,
        "text/html"
      );

    const incomingMain =
      getMainContent(
        parsedDocument
      );

    if (!incomingMain) {
      throw new Error(
        "Incoming main content not found"
      );
    }

    /*
     * First try to find the exact same
     * Shopify section using its ID.
     */
    let incomingCollectionSection = null;

    if (currentCollectionSection.id) {

      incomingCollectionSection =
        incomingMain.querySelector(
          "#" +
          CSS.escape(
            currentCollectionSection.id
          )
        );

    }

    /*
     * Fallback:
     * find the section containing
     * the sidebar in the fetched page.
     */
    if (!incomingCollectionSection) {

      const incomingSidebar =
        incomingMain.querySelector(
          ".tp-shop-sidebar"
        );

      incomingCollectionSection =
        incomingSidebar
          ? incomingSidebar.closest(
              ".shopify-section"
            )
          : null;

    }

    if (!incomingCollectionSection) {
      throw new Error(
        "Collection section not found"
      );
    }

    /*
     * Copy ONLY collection section.
     *
     * Popular Brands and all other
     * sliders remain untouched.
     */
    const newCollectionSection =
      document.importNode(
        incomingCollectionSection,
        true
      );

    currentCollectionSection.replaceWith(
      newCollectionSection
    );

    document.title =
      parsedDocument.title;

    updateCanonical(
      parsedDocument
    );

    if (updateHistory) {

      window.history.pushState(
        {
          homtechCollectionAjax: true
        },
        "",
        requestedUrl
      );

    }

    /*
     * Re-init our custom collection
     * filters only.
     */
    updateActiveCategoryLinks(
      newCollectionSection
    );

    notifyThemeAfterAjax(
      newCollectionSection
    );

    if (scrollToProducts) {

      const position =
        newCollectionSection
          .getBoundingClientRect()
          .top +
        window.pageYOffset -
        30;

      window.scrollTo({
        top: Math.max(
          0,
          position
        ),
        behavior: "smooth"
      });

    }

  } catch (error) {

    if (
      error &&
      error.name === "AbortError"
    ) {
      return;
    }

    console.error(
      "Collection AJAX:",
      error
    );

    /*
     * Safe fallback.
     */
    window.location.href =
      requestedUrl;

  } finally {

    if (
      thisRequest ===
      requestCounter
    ) {

      currentRequest = null;

      setLoading(false);

    }

  }
}

  /* =========================================================
     CATEGORY ACCORDION
  ========================================================= */

  document.addEventListener(
    "click",
    (event) => {
      const toggleButton =
        event.target.closest(
          ".ht-category-tree__toggle"
        );

      if (!toggleButton) return;

      event.preventDefault();

      const categoryItem =
        toggleButton.closest(
          ".ht-category-tree__item"
        );

      const submenuId =
        toggleButton.getAttribute(
          "aria-controls"
        );

      const submenu =
        submenuId
          ? document.getElementById(
              submenuId
            )
          : null;

      if (
        !categoryItem ||
        !submenu
      ) {
        return;
      }

      const willOpen =
        !categoryItem
          .classList
          .contains(
            "is-open"
          );

      categoryItem
        .classList
        .toggle(
          "is-open",
          willOpen
        );

      toggleButton.setAttribute(
        "aria-expanded",
        willOpen
          ? "true"
          : "false"
      );

      submenu.hidden =
        !willOpen;
    }
  );

  /* =========================================================
     CATEGORY + FILTER LINKS
  ========================================================= */

  document.addEventListener(
    "click",
    (event) => {
      const link =
        event.target.closest(
          `${CATEGORY_LINK_SELECTOR}, ${FILTER_LINK_SELECTOR}`
        );

      if (!link) return;

      if (
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }

      if (
        link.target === "_blank" ||
        link.hasAttribute(
          "download"
        )
      ) {
        return;
      }

      let url;

      try {
        url =
          new URL(
            link.href,
            window.location.href
          );
      } catch (error) {
        return;
      }

      if (
        url.origin !==
        window.location.origin
      ) {
        return;
      }

      event.preventDefault();

      /*
       * Price reset:
       * normal navigation for stability.
       */
      if (
        link.closest(
          "[data-ht-price-filter]"
        )
      ) {
        window.location.href =
          url.href;

        return;
      }

      const isCategoryLink =
        link.matches(
          CATEGORY_LINK_SELECTOR
        );

      loadCollectionPage(
        url.href,
        {
          updateHistory:
            true,

          scrollToProducts:
            isCategoryLink
        }
      );
    }
  );

  /* =========================================================
     BACK / FORWARD
  ========================================================= */

  window.addEventListener(
    "popstate",
    () => {
      loadCollectionPage(
        window.location.href,
        {
          updateHistory:
            false,

          scrollToProducts:
            false
        }
      );
    }
  );

  /* =========================================================
     PRICE FILTER
  ========================================================= */

  function getPriceElements(
    filter
  ) {
    return {
      minRange:
        filter.querySelector(
          "[data-price-min]"
        ) ||
        filter.querySelector(
          "[data-price-min-range]"
        ),

      maxRange:
        filter.querySelector(
          "[data-price-max]"
        ) ||
        filter.querySelector(
          "[data-price-max-range]"
        ),

      progress:
        filter.querySelector(
          "[data-price-progress]"
        ) ||
        filter.querySelector(
          "[data-price-active-line]"
        ),

      minLabel:
        filter.querySelector(
          "[data-price-min-label]"
        ),

      maxLabel:
        filter.querySelector(
          "[data-price-max-label]"
        ),

      minField:
        filter.querySelector(
          "[data-price-min-field]"
        ),

      maxField:
        filter.querySelector(
          "[data-price-max-field]"
        )
    };
  }

  function readDataNumber(
    element,
    keys,
    fallback = 0
  ) {
    for (
      const key of keys
    ) {
      const value =
        element.dataset[key];

      if (
        value !== undefined &&
        value !== null &&
        String(value)
          .trim() !== ""
      ) {
        const number =
          Number(value);

        if (
          Number.isFinite(
            number
          )
        ) {
          return number;
        }
      }
    }

    return fallback;
  }

  function formatPrice(
    value,
    currency
  ) {
    const number =
      Number(value || 0);

    const formatted =
      number.toLocaleString(
        "en-US",
        {
          minimumFractionDigits:
            2,

          maximumFractionDigits:
            2
        }
      );

    return currency
      ? `${currency} ${formatted}`
      : formatted;
  }

  function findFirstCollectionProduct(
    doc
  ) {
    const preferred =
      doc.querySelector(
        ".tp-product-item-2.tps__product-item[data-product-handle]"
      );

    if (preferred) {
      return preferred;
    }

    const candidates =
      Array.from(
        doc.querySelectorAll(
          ".tps__product-item[data-product-handle]"
        )
      );

    return (
      candidates.find(
        (item) =>
          !item.closest(
            ".tp-shop-sidebar"
          )
      ) || null
    );
  }

  async function getActualCollectionMinPrice(
    filter
  ) {
    const minParam =
      filter.dataset.minParam;

    const maxParam =
      filter.dataset.maxParam;

    const fallbackMin =
      readDataNumber(
        filter,
        [
          "fallbackMin"
        ],
        0
      );

    const url =
      new URL(
        window.location.href
      );

    /*
     * Keep POE / category / other filters.
     * Remove price only.
     */
    if (minParam) {
      url.searchParams.delete(
        minParam
      );
    }

    if (maxParam) {
      url.searchParams.delete(
        maxParam
      );
    }

    url.searchParams.delete(
      "page"
    );

    url.searchParams.set(
      "sort_by",
      "price-ascending"
    );

    const cacheKey =
      url.toString();

    if (
      priceMinCache.has(
        cacheKey
      )
    ) {
      return priceMinCache.get(
        cacheKey
      );
    }

    try {
      const response =
        await fetch(
          url.toString(),
          {
            method: "GET",

            credentials:
              "same-origin",

            headers: {
              Accept:
                "text/html",

              "X-Requested-With":
                "XMLHttpRequest"
            }
          }
        );

      if (!response.ok) {
        throw new Error(
          "Could not load sorted collection"
        );
      }

      const html =
        await response.text();

      const parsedDocument =
        new DOMParser()
          .parseFromString(
            html,
            "text/html"
          );

      const firstProduct =
        findFirstCollectionProduct(
          parsedDocument
        );

      if (!firstProduct) {
        throw new Error(
          "No collection product found"
        );
      }

      /*
       * If product-item has:
       * data-product-price="{{ product.price }}"
       */
      const directCents =
        Number(
          firstProduct
            .dataset
            .productPrice || 0
        );

      if (
        Number.isFinite(
          directCents
        ) &&
        directCents > 0
      ) {
        const directPrice =
          directCents / 100;

        priceMinCache.set(
          cacheKey,
          directPrice
        );

        return directPrice;
      }

      /*
       * Fallback:
       * use product handle.
       */
      const handle =
        firstProduct.getAttribute(
          "data-product-handle"
        );

      if (!handle) {
        throw new Error(
          "Product handle not found"
        );
      }

      const root =
        window.Shopify &&
        Shopify.routes &&
        Shopify.routes.root
          ? Shopify.routes.root
          : "/";

      const productResponse =
        await fetch(
          `${root}products/${encodeURIComponent(
            handle
          )}.js`,
          {
            credentials:
              "same-origin",

            headers: {
              Accept:
                "application/json"
            }
          }
        );

      if (
        !productResponse.ok
      ) {
        throw new Error(
          "Could not load cheapest product"
        );
      }

      const product =
        await productResponse.json();

      const cents =
        Number(
          product.price_min ||
          product.price ||
          0
        );

      if (
        !Number.isFinite(
          cents
        ) ||
        cents <= 0
      ) {
        throw new Error(
          "Invalid cheapest product price"
        );
      }

      const actualMin =
        cents / 100;

      priceMinCache.set(
        cacheKey,
        actualMin
      );

      return actualMin;

    } catch (error) {
      console.warn(
        "HomTech minimum price:",
        error
      );

      if (
        fallbackMin > 0
      ) {
        return fallbackMin;
      }

      return null;
    }
  }

  function updatePriceUI(
    filter
  ) {
    const {
      minRange,
      maxRange,
      progress,
      minLabel,
      maxLabel,
      minField,
      maxField
    } = getPriceElements(
      filter
    );

    if (
      !minRange ||
      !maxRange
    ) {
      return;
    }

    const minLimit =
      Number(
        minRange.min
      );

    const maxLimit =
      Number(
        maxRange.max
      );

    let minValue =
      Number(
        minRange.value
      );

    let maxValue =
      Number(
        maxRange.value
      );

    if (
      !Number.isFinite(
        minValue
      )
    ) {
      minValue =
        minLimit;
    }

    if (
      !Number.isFinite(
        maxValue
      )
    ) {
      maxValue =
        maxLimit;
    }

    if (
      minValue >
      maxValue
    ) {
      minValue =
        maxValue;

      minRange.value =
        String(
          minValue
        );
    }

    if (
      maxValue <
      minValue
    ) {
      maxValue =
        minValue;

      maxRange.value =
        String(
          maxValue
        );
    }

    const distance =
      Math.max(
        maxLimit -
          minLimit,
        1
      );

    const left =
      (
        (
          minValue -
          minLimit
        ) /
        distance
      ) * 100;

    const right =
      100 -
      (
        (
          maxValue -
          minLimit
        ) /
        distance
      ) * 100;

    if (progress) {
      progress.style.left =
        `${Math.max(
          0,
          Math.min(
            100,
            left
          )
        )}%`;

      progress.style.right =
        `${Math.max(
          0,
          Math.min(
            100,
            right
          )
        )}%`;
    }

    const currency =
      filter.dataset.currency ||
      "";

    if (minLabel) {
      minLabel.textContent =
        formatPrice(
          minValue,
          currency
        );
    }

    if (maxLabel) {
      maxLabel.textContent =
        formatPrice(
          maxValue,
          currency
        );
    }

    if (minField) {
      minField.value =
        String(
          minValue
        );
    }

    if (maxField) {
      maxField.value =
        String(
          maxValue
        );
    }

    /*
     * Help selecting handles
     * when they are very close.
     */
    if (
      minValue >
      maxLimit -
        distance * 0.1
    ) {
      minRange.style.zIndex =
        "6";
    } else {
      minRange.style.zIndex =
        "4";
    }
  }

  async function initPriceFilter(
    filter
  ) {
    if (
      !filter ||
      filter.dataset
        .priceReady ===
        "true"
    ) {
      return;
    }

    filter.dataset.priceReady =
      "true";

    const {
      minRange,
      maxRange,
      minLabel,
      maxLabel
    } = getPriceElements(
      filter
    );

    if (
      !minRange ||
      !maxRange
    ) {
      return;
    }

    const currency =
      filter.dataset.currency ||
      "";

    const fullMax =
      readDataNumber(
        filter,
        [
          "fullMax",
          "maxPrice"
        ],
        Number(
          maxRange.max
        ) || 0
      );

    const fallbackMin =
      readDataNumber(
        filter,
        [
          "fallbackMin"
        ],
        0
      );

    const selectedMin =
      readDataNumber(
        filter,
        [
          "selectedMin",
          "activeMin"
        ],
        0
      );

    const selectedMax =
      readDataNumber(
        filter,
        [
          "selectedMax",
          "activeMax"
        ],
        0
      );

    /*
     * Never leave ...
     */
    if (minLabel) {
      minLabel.textContent =
        fallbackMin > 0
          ? formatPrice(
              fallbackMin,
              currency
            )
          : "Min";
    }

    if (
      maxLabel &&
      fullMax > 0
    ) {
      maxLabel.textContent =
        formatPrice(
          fullMax,
          currency
        );
    }

    const actualMin =
      await getActualCollectionMinPrice(
        filter
      );

    /*
     * Section may have been replaced
     * while awaiting fetch.
     */
    if (
      !document.documentElement
        .contains(
          filter
        )
    ) {
      return;
    }

    let realMin =
      Number(
        actualMin
      );

    if (
      !Number.isFinite(
        realMin
      ) ||
      realMin <= 0
    ) {
      realMin =
        fallbackMin > 0
          ? fallbackMin
          : selectedMin > 0
            ? selectedMin
            : 1;
    }

    let realMax =
      Number(
        fullMax
      );

    if (
      !Number.isFinite(
        realMax
      ) ||
      realMax <= 0
    ) {
      realMax =
        Number(
          maxRange.max
        ) ||
        realMin;
    }

    if (
      realMax <
      realMin
    ) {
      realMax =
        realMin;
    }

    minRange.min =
      String(
        realMin
      );

    minRange.max =
      String(
        realMax
      );

    maxRange.min =
      String(
        realMin
      );

    maxRange.max =
      String(
        realMax
      );

    const startMin =
      selectedMin >=
        realMin &&
      selectedMin <=
        realMax
        ? selectedMin
        : realMin;

    const startMax =
      selectedMax >=
        startMin &&
      selectedMax <=
        realMax
        ? selectedMax
        : realMax;

    minRange.value =
      String(
        startMin
      );

    maxRange.value =
      String(
        startMax
      );

    filter.dataset.realMin =
      String(
        realMin
      );

    filter.dataset.fullMax =
      String(
        realMax
      );

    updatePriceUI(
      filter
    );
  }

  function initAllPriceFilters(
    root = document
  ) {
    root
      .querySelectorAll(
        "[data-ht-price-filter]"
      )
      .forEach(
        initPriceFilter
      );
  }

  function getRangeFromEvent(
    target
  ) {
    return target.closest(
      [
        "[data-price-min]",
        "[data-price-max]",
        "[data-price-min-range]",
        "[data-price-max-range]"
      ].join(",")
    );
  }

  /*
   * Update visual range
   * while dragging.
   */
  document.addEventListener(
    "input",
    (event) => {
      const range =
        getRangeFromEvent(
          event.target
        );

      if (!range) return;

      const filter =
        range.closest(
          "[data-ht-price-filter]"
        );

      if (!filter) return;

      const {
        minRange,
        maxRange
      } = getPriceElements(
        filter
      );

      if (
        !minRange ||
        !maxRange
      ) {
        return;
      }

      if (
        range === minRange &&
        Number(
          minRange.value
        ) >
        Number(
          maxRange.value
        )
      ) {
        minRange.value =
          maxRange.value;
      }

      if (
        range === maxRange &&
        Number(
          maxRange.value
        ) <
        Number(
          minRange.value
        )
      ) {
        maxRange.value =
          minRange.value;
      }

      updatePriceUI(
        filter
      );
    }
  );

  /*
   * Apply price after
   * releasing handle.
   */
  document.addEventListener(
    "change",
    (event) => {
      const range =
        getRangeFromEvent(
          event.target
        );

      if (!range) return;

      const filter =
        range.closest(
          "[data-ht-price-filter]"
        );

      if (!filter) return;

      const {
        minRange,
        maxRange
      } = getPriceElements(
        filter
      );

      if (
        !minRange ||
        !maxRange
      ) {
        return;
      }

      const minParam =
        filter.dataset.minParam;

      const maxParam =
        filter.dataset.maxParam;

      if (
        !minParam ||
        !maxParam
      ) {
        console.warn(
          "HomTech price filter parameter names are missing."
        );

        return;
      }

      const realMin =
        readDataNumber(
          filter,
          [
            "realMin"
          ],
          Number(
            minRange.min
          )
        );

      const fullMax =
        readDataNumber(
          filter,
          [
            "fullMax",
            "maxPrice"
          ],
          Number(
            maxRange.max
          )
        );

      const minValue =
        Number(
          minRange.value
        );

      const maxValue =
        Number(
          maxRange.value
        );

      const url =
        new URL(
          window.location.href
        );

      url.searchParams.delete(
        "page"
      );

      /*
       * Minimum
       */
      if (
        minValue >
        realMin +
          0.0001
      ) {
        url.searchParams.set(
          minParam,
          String(
            minValue
          )
        );
      } else {
        url.searchParams.delete(
          minParam
        );
      }

      /*
       * Maximum
       */
      if (
        maxValue <
        fullMax -
          0.0001
      ) {
        url.searchParams.set(
          maxParam,
          String(
            maxValue
          )
        );
      } else {
        url.searchParams.delete(
          maxParam
        );
      }

      /*
       * IMPORTANT:
       * Price uses normal page reload
       * for maximum stability.
       */
      window.location.href =
        url.toString();
    }
  );

  /* =========================================================
     EVENTS AFTER AJAX
  ========================================================= */

  document.addEventListener(
    "ht:collection:loaded",
    (event) => {
      const root =
        event.detail?.main ||
        getMainContent() ||
        document;

      initAllPriceFilters(
        root
      );

      updateActiveCategoryLinks(
        root
      );
    }
  );

  document.addEventListener(
    "shopify:section:load",
    (event) => {
      initAllPriceFilters(
        event.target ||
        document
      );
    }
  );

  /* =========================================================
     INITIAL LOAD
  ========================================================= */

  function init() {
    updateActiveCategoryLinks(
      document
    );

    initAllPriceFilters(
      document
    );
  }

  if (
    document.readyState ===
    "loading"
  ) {
    document.addEventListener(
      "DOMContentLoaded",
      init,
      {
        once: true
      }
    );
  } else {
    init();
  }

})();